import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Button } from '@frontend/design-system';
import { useCoachmarkTooltip } from '../hooks/use-coachmark-tooltip';
import { CoachmarkTooltip } from './coachmark-tooltip';

export interface CompareLocationsButtonProps {
  /** Whether the location-comparison view is currently active. */
  comparingLocations: boolean;
  /** Toggles the comparison view. The consuming feature owns the route navigation. */
  onToggle: () => void;
  /** Unique key used to frequency-cap the one-time coachmark tooltip. */
  coachmarkKey: string;
}

/**
 * Shared button that toggles an analytics module between its chart view and the
 * location-comparison view, with a one-time coachmark tooltip. Each feature owns
 * its own visibility gates and typed route navigation via `onToggle`.
 */
export const CompareLocationsButton = ({ comparingLocations, onToggle, coachmarkKey }: CompareLocationsButtonProps) => {
  const { t } = useTranslation('analytics');
  const { shouldShow, recordShown, recordDismiss } = useCoachmarkTooltip(coachmarkKey);

  const tooltipText = comparingLocations
    ? t('Switch to Chart view from here.', { ns: 'analytics' })
    : t('Switch to Location Comparison view from here.', { ns: 'analytics' });

  return (
    <div css={wrapperStyles}>
      {comparingLocations ? (
        <Button variant='secondary' iconName='x' css={activeButtonStyles} onClick={onToggle} />
      ) : (
        <Button variant='secondary' iconName='locations' onClick={onToggle}>
          {t('Compare locations')}
        </Button>
      )}

      {shouldShow && (
        <CoachmarkTooltip
          key={String(comparingLocations)}
          text={tooltipText}
          placement='left'
          dotCss={dotStyles}
          onShow={recordShown}
          onDismiss={recordDismiss}
        />
      )}
    </div>
  );
};

const wrapperStyles = css`
  position: relative;
  display: inline-flex;
`;

const activeButtonStyles = css`
  border: 1px solid var(--colors-neutral-30);
`;

const dotStyles = css`
  top: 50%;
  left: -12px;
  transform: translateY(-50%);
`;
